import React from 'react';
import { connect } from 'react-redux';
import { useTranslation, Trans } from 'react-i18next';
import { bindActionCreators } from 'redux';
import { useFormContext } from 'react-hook-form';
import { Accordion, Box, Tooltip, InfoIcon } from 'edenred-ui';
import { useTheme } from 'styled-components';

import { FormFields } from '@epi-forms/helpers';

import { formatNumeric, formatCurrency } from '../../helpers/numeral';
import { VoucherTypes } from '../../constants/voucherTypes';
import * as navigationActions from '../../actions/navigation';

import './BenefitSummaryContent.scss';
import { BenefitSummaryContentProps } from '@epi-models/components/BenefitSummary';

const LOCALE_PREFIX = 'components.benefit_summary';

function BenefitSummaryContent({
  showCreditCardFee,
  voucherTypeKey,
  orderSummary: {
    creditCardFeePercent,
    creditCardFee,
    withoutReference = false,
    vatFeePercent,
    vatFeeAmount,
    handlingFee
  },
  voucherOrderPayment
}: BenefitSummaryContentProps) {
  const { watch } = useFormContext();
  const { t } = useTranslation();
  const { gray50, sizeMedium, primaryFrontColor } = useTheme();

  const quantityValue =
    watch(FormFields.VoucherQuantity) || voucherOrderPayment?.totalQuantity;
  const voucherWorthValue =
    watch(FormFields.VoucherWorth) ||
    voucherOrderPayment?.faceValue ||
    (voucherOrderPayment?.totalAmount ?? 0) /
      (voucherOrderPayment?.totalQuantity ?? 1);
  const sumValue = quantityValue * voucherWorthValue;
  const orderTotalWithoutVat = sumValue + handlingFee + creditCardFee;
  const totalAmountWithFees = orderTotalWithoutVat + vatFeeAmount;
  const formattedQuantity = `${formatNumeric(quantityValue, '0,0')}`;
  const formattedFaceValue = `${formatCurrency(voucherWorthValue)}`;
  const formattedSumVouchers = `${formatCurrency(sumValue)}`;

  return (
    <Box
      px={3}
      className="BenefitSummaryContent benefit-summary-content"
      id="BenefitSummaryContent"
    >
      <Box
        display="flex"
        justifyContent="space-between"
        color={gray50}
        fontSize={sizeMedium}
      >
        <Box flex={2}>
          {t(
            `${LOCALE_PREFIX}.vouchers_total[${voucherTypeKey.toLowerCase()}]`,
            { quantity: formattedQuantity, faceValue: formattedFaceValue }
          )}
        </Box>
        <Box flex={1} textAlign="right">
          {t(`${LOCALE_PREFIX}.vat`, { vatFeePercent: 0 })}
        </Box>
        <Box flex={1} textAlign="right">
          {formattedSumVouchers}
        </Box>
      </Box>
      <Box
        display="flex"
        justifyContent="space-between"
        color={gray50}
        fontSize={sizeMedium}
      >
        <Box flex={2} className={withoutReference ? 'blue-text' : ''}>
          {t(`${LOCALE_PREFIX}.handling_fee`)}
        </Box>
        <Box flex={1} textAlign="right">
          {t(`${LOCALE_PREFIX}.vat`, { vatFeePercent })}
        </Box>
        <Box flex={1} textAlign="right">
          {handlingFee || '-'} €
        </Box>
      </Box>
      <Box fontSize={12.5}>
        <Trans
          defaults={t(
            `${LOCALE_PREFIX}.${
              VoucherTypes[voucherTypeKey] === VoucherTypes.Lunch
                ? 'lunch_price_list_and_fee'
                : 'recreational_price_list_and_fee'
            }`
          )}
          components={{
            a: (
              <a
                target="_blank"
                rel="noreferrer"
                href={t(
                  `${LOCALE_PREFIX}.${
                    VoucherTypes[voucherTypeKey] === VoucherTypes.Lunch
                      ? 'lunch_price_list_and_fee_url'
                      : 'recreational_price_list_and_fee_url'
                  }`
                )}
              />
            )
          }}
        />
      </Box>
      <hr />
      <Box
        display="flex"
        justifyContent="space-between"
        color={gray50}
        fontSize={sizeMedium}
      >
        <Box flex={2}>{t(`${LOCALE_PREFIX}.total_without_vat`)}</Box>
        <Box flex={1} textAlign="right">
          {formatCurrency(orderTotalWithoutVat)}
        </Box>
      </Box>
      <Box
        display="flex"
        justifyContent="space-between"
        color={gray50}
        fontSize={sizeMedium}
      >
        <div>{t(`${LOCALE_PREFIX}.total_vat`, { vatFeePercent })}</div>
        <div>{formatCurrency(vatFeeAmount)}</div>
      </Box>
      <Accordion
        expanded={showCreditCardFee}
        hideArrow
        summaryChild={<span />}
        detailsChild={
          <div>
            <hr />
            <Box
              display="flex"
              justifyContent="space-between"
              color={gray50}
              fontSize={sizeMedium}
            >
              <div className="blue-text">
                {t(`${LOCALE_PREFIX}.credit_card_fee`)}{' '}
              </div>
              {creditCardFeePercent > 0 && (
                <Tooltip
                  arrow
                  placement="top"
                  id={`credit-card-tooltip`}
                  componentsProps={{
                    tooltip: {
                      sx: {
                        maxWidth: '500px'
                      }
                    }
                  }}
                  title={t(`${LOCALE_PREFIX}.credit_card_fee_desc`, {
                    value: formatNumeric(creditCardFeePercent, '0.00')
                  })}
                >
                  <Box id={`credit-card}-info-icon`}>
                    <InfoIcon color={primaryFrontColor} />
                  </Box>
                </Tooltip>
              )}
              <div className="blue-text">
                {totalAmountWithFees ? formatCurrency(creditCardFee) : '?'}
              </div>
            </Box>
          </div>
        }
      />
      <hr className="gray" />
      <Box
        display="flex"
        justifyContent="space-between"
        color={gray50}
        fontSize={sizeMedium}
      >
        <Box flex={1}>{t(`${LOCALE_PREFIX}.order_total`)}</Box>
        <Box flex={1} textAlign="right">
          {formatCurrency(totalAmountWithFees)}
        </Box>
      </Box>
    </Box>
  );
}

const mapDispatchToProps = dispatch =>
  bindActionCreators({ ...navigationActions }, dispatch);

const ConnectedBenefitSummaryContent = connect(
  null,
  mapDispatchToProps
)(BenefitSummaryContent);

export {
  ConnectedBenefitSummaryContent as BenefitSummaryContent,
  BenefitSummaryContent as BenefitSummaryContentTest
};
